import { Alert, Button, Stack, Typography } from "@mui/material";
import { t } from "i18next";
import React from "react";
import { sendPublicFeedback } from "src/api/system";
import {
  buildComment,
  buildCrashSummary,
  extractMainErrorMessage,
  extractStackHeadline,
  extractUsefulComponentStack,
  limitText,
  uniqueKey,
} from "./error-functions";
import * as Sentry from "@sentry/react";
type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  errorMessage: string;
  showFullError: boolean;
};

export default class AppErrorBoundary extends React.Component<Props, State> {
  private hasReported = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: "",
      showFullError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      errorMessage: error?.message || "Unknown error",
      showFullError: false,
    };
  }
  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);

    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo?.componentStack,
        source: "AppErrorBoundary",
        url: window.location.href,
        pathname: window.location.pathname,
      },
    });

    if (this.hasReported) return;
    this.hasReported = true;

    try {
      const currentUrl =
        typeof window !== "undefined" ? window.location.href : null;
      const currentPath =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "";
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : null;
      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : null;
      const locale = typeof navigator !== "undefined" ? navigator.language : "";

      const comment = limitText(
        [
          `source: AppErrorBoundary`,
          `ts: ${new Date().toISOString()}`,
          currentUrl ? `url: ${currentUrl}` : "",
          currentPath ? `path: ${currentPath}` : "",
          pathname ? `pathname: ${pathname}` : "",
          locale ? `locale: ${locale}` : "",
          userAgent ? `ua: ${userAgent}` : "",
          `message: ${extractMainErrorMessage(
            error?.message || "Unknown error"
          )}`,
          error?.message ? `raw_message: ${limitText(error.message, 400)}` : "",
          error?.stack ? `stack: ${extractStackHeadline(error.stack)}` : "",
          errorInfo?.componentStack
            ? `component: ${extractUsefulComponentStack(
                errorInfo.componentStack
              )}`
            : "",
        ]
          .filter(Boolean)
          .join("\n"),
        2000
      );

      const payload = {
        feature_key: uniqueKey("frontend_react_error_boundary"),
        reasons: ["react_crash"],
        userId: 999999999,
        usefulness: 3,
        comment,
        context: {},
      };

      await sendPublicFeedback(payload);
    } catch (reportError) {
      console.error("Failed to report frontend error:", reportError);
    }
  }

  render() {
    if (this.state.hasError) {
      const fullMessage =
        this.state.errorMessage || t("unexpected_error_occured");
      const isLongMessage = fullMessage.length > 300;
      const displayedMessage =
        isLongMessage && !this.state.showFullError
          ? `${fullMessage.slice(0, 300)}...`
          : fullMessage;

      return (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            width: "90%",
            maxWidth: 720,
          }}
        >
          <Alert
            severity="error"
            variant="filled"
            sx={{
              borderRadius: 3,
              boxShadow: 6,
              alignItems: "flex-start",
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Typography variant="h5" fontWeight={700}>
                {t("sportprovider_load_error")}
              </Typography>

              <Typography variant="body1">{displayedMessage}</Typography>

              {isLongMessage && (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() =>
                    this.setState((prev) => ({
                      showFullError: !prev.showFullError,
                    }))
                  }
                  sx={{
                    alignSelf: "flex-start",
                    color: "common.white",
                    textDecoration: "underline",
                    p: 0,
                    minWidth: "auto",
                  }}
                >
                  {this.state.showFullError ? t("show_less") : t("show_more")}
                </Button>
              )}

              <Stack direction="column" spacing={1.5}>
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={() => (window.location.href = "/")}
                  sx={{
                    color: "error.main",
                    fontWeight: 700,
                    bgcolor: "common.white",
                    "&:hover": {
                      bgcolor: "grey.100",
                    },
                  }}
                >
                  {t("go_to_homepage")}
                </Button>

                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => window.location.reload()}
                  sx={{
                    color: "error.main",
                    fontWeight: 700,
                    bgcolor: "common.white",
                    "&:hover": {
                      bgcolor: "grey.100",
                    },
                  }}
                >
                  {t("error_try_again")}
                </Button>
              </Stack>
            </Stack>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
